import { 
  MAGIC, VERSION, 
  decodeObject,
  calculateChecksum, base64urlDecode,
  readUint16, readUint8, readUint32
} from './binary.js';

// ---------- Binary Capsule Format ----------
// [ 0..3 ]  Magic "JSC2" (Jucie State Capsule v2)
// [ 4..5 ]  u16 version (big-endian) = 2
// [ 6 ]     u8 flags (reserved for future use)
// [ 7..10 ] u32 checksum (simple checksum)
// [11..14 ] u32 payloadLength (big-endian)
// [15.. ]   payload bytes (binary encoded data)

// ---------- Public API ----------

/**
 * Import a binary capsule string created by exportState.
 * Returns decoded data object with metadata.
 * @param {String} capsuleString - Base64url encoded capsule
 * @param {Function} [cb] - Optional callback(data)
 */
export async function unpack(capsuleString, cb) {
  // 1) Decode
  const capsule = base64urlDecode(capsuleString);

  // 2) Sanity checks
  if (capsule.length < 15) {
    throw new Error("Invalid capsule: too short");
  }

  // Magic
  for (let i = 0; i < 4; i++) {
    if (capsule[i] !== MAGIC[i]) throw new Error("Invalid capsule: bad magic");
  }

  // Version
  const version = readUint16(capsule, 4);
  if (version !== VERSION) {
    throw new Error(`Unsupported capsule version: ${version}`);
  }

  // Flags (reserved)
  const flags = readUint8(capsule, 6);

  // Checksum
  const storedChecksum = readUint32(capsule, 7);

  // Payload length
  const payloadLength = readUint32(capsule, 11);
  if (payloadLength < 0) throw new Error("Invalid capsule: negative payload length");
  if (capsule.length !== 15 + payloadLength) throw new Error("Invalid capsule: length mismatch");

  const payload = capsule.slice(15);

  // 3) Verify checksum
  const actualChecksum = calculateChecksum(payload);
  if (storedChecksum !== actualChecksum) {
    throw new Error("Integrity check failed: checksum mismatch");
  }

  // 4) Decode payload
  let offset = 0;
  
  // Decode data
  const dataLength = readUint32(payload, offset);
  offset += 4;
  
  if (offset + dataLength > payload.length) {
    throw new Error(`Data length ${dataLength} exceeds payload bounds at offset ${offset}`);
  }
  
  const [data] = decodeObject(payload.slice(offset, offset + dataLength));

  // 5) Call callback if provided
  if (cb) cb(data);

  return {
    data,   // Add 'data' key for generic usage
    meta: {
      version,
      flags,
      bytes: capsule.length,
      checksum: actualChecksum.toString(16),
    },
  };
}